import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { Box, IconButton, MenuItem, Typography, TextField, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { binRequestsPer15Min, getRidePointsTimeRange, parseRequestTimeToMs } from './utils';
import type { SimulationActivity } from '../../../../types/simulation';

import { Icon } from '@liftango/ui-icons';
const TARMAC = '#3A3A48';
const ORANGE = '#EE7334';
const FONT_FAMILY = '"Roboto", sans-serif';
const EXPANDED_HEIGHT = 168;
const TAB_HEIGHT = 28;
const HEADER_HEIGHT = 40;
const PLAY_INTERVAL_MS = 50;
/** Simulated minutes per real second. */
const PLAY_SPEEDS = [2, 6, 15, 40] as const;
const DEFAULT_PLAY_SPEED = 15;

export interface TimelineControlProps {
  ridePoints: any[];
  /** Vehicle activities; playback runs until the last one ends, not the last request. */
  activities?: SimulationActivity[] | null;
  /** Optional: controlled current time (e.g. for syncing map filter) */
  currentTime?: Date | null;
  onTimeChange?: (time: Date) => void;
  /** Time window (minutes) to show trips on map: [currentTime - window, currentTime] */
  windowMinutes?: number;
  onWindowMinutesChange?: (minutes: number) => void;
  /** When true, render only inner content (no outer container, no collapse). For use inside AnalyticsDeck. */
  embedded?: boolean;
}

/**
 * Timeline: collapsible bottom bar with Play/Pause, time display, and 15-min histogram scrubber.
 * Tarmac theme; Liftango Orange for play and window highlight.
 */
const TimelineControl: React.FC<TimelineControlProps> = ({
  ridePoints,
  activities,
  currentTime: controlledTime,
  onTimeChange,
  windowMinutes = 24 * 60, // 24 hours: full day by default
  embedded = false,
}) => {
  const { t } = useTranslation('simulation');
  const [isExpanded, setIsExpanded] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [internalTime, setInternalTime] = useState<Date>(() => new Date());
  const [playSpeed, setPlaySpeed] = useState<number>(DEFAULT_PLAY_SPEED);
  const playIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const currentTime = controlledTime ?? internalTime;
  const currentTimeRef = useRef(currentTime);
  currentTimeRef.current = currentTime;

  const setCurrentTime = useCallback(
    (t: Date) => {
      if (onTimeChange) onTimeChange(t);
      else setInternalTime(t);
    },
    [onTimeChange]
  );

  // Build 15-min bins from the ride data.
  // We render only the bins that actually contain rides (no gap-filling between empty slots).
  // This prevents a data set spanning many days from creating hundreds or thousands of DOM
  // elements for empty 15-min slots, which would freeze the browser.
  // A hard cap of MAX_BINS is applied as a backstop in case of anomalous data.
  const MAX_BINS = 200; // ~50 hours of 15-min slots — sufficient for any single simulation
  const { bins, timeRange } = useMemo(() => {
    const points = ridePoints ?? [];
    const range = getRidePointsTimeRange(points);
    if (!range || points.length === 0) {
      return { bins: [] as { timestampMs: number; label: string; count: number }[], timeRange: null };
    }
    const binned = binRequestsPer15Min(points);
    if (binned.length === 0) {
      return { bins: [], timeRange: range };
    }
    // Trim to the most recent MAX_BINS if data is unexpectedly large
    const trimmed = binned.length > MAX_BINS ? binned.slice(binned.length - MAX_BINS) : binned;
    return { bins: trimmed, timeRange: range };
  }, [ridePoints]);

  // Vehicles drive before the first request and after the last one, so play over both.
  const playRange = useMemo(() => {
    if (!timeRange) return null;
    let { minMs, maxMs } = timeRange;
    (activities ?? []).forEach((a) => {
      const startMs = parseRequestTimeToMs(a.activity_start_time);
      const endMs = parseRequestTimeToMs(a.activity_end_time ?? undefined) ?? startMs;
      if (startMs != null) minMs = Math.min(minMs, startMs);
      if (endMs != null) maxMs = Math.max(maxMs, endMs);
    });
    return { minMs, maxMs };
  }, [timeRange, activities]);

  // Play loop: advance time every tick using ref so we always have latest value
  useEffect(() => {
    if (!isPlaying) {
      if (playIntervalRef.current) {
        clearInterval(playIntervalRef.current);
        playIntervalRef.current = null;
      }
      return;
    }
    // Step by real elapsed time: ticks arrive late on big runs, and the speed must still hold.
    let lastTick = performance.now();
    playIntervalRef.current = setInterval(() => {
      const now = performance.now();
      const nextMs = currentTimeRef.current.getTime() + playSpeed * 60 * (now - lastTick);
      lastTick = now;
      if (playRange && nextMs >= playRange.maxMs) {
        setCurrentTime(new Date(playRange.maxMs));
        setIsPlaying(false);
        return;
      }
      setCurrentTime(new Date(nextMs));
    }, PLAY_INTERVAL_MS);
    return () => {
      if (playIntervalRef.current) clearInterval(playIntervalRef.current);
    };
  }, [isPlaying, playSpeed, playRange, setCurrentTime]);

  const handlePlayToggle = () => {
    // Play from the end restarts at the beginning.
    if (!isPlaying && playRange && currentTimeRef.current.getTime() >= playRange.maxMs) {
      setCurrentTime(new Date(playRange.minMs));
    }
    setIsPlaying((p) => !p);
  };

  // Initialize internal currentTime to end of range so the default 24h window shows the full day highlighted
  const hasInitialized = useRef(false);
  useEffect(() => {
    if (controlledTime != null || !timeRange || bins.length === 0) return;
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      setInternalTime(new Date(timeRange.maxMs));
    }
  }, [timeRange, bins.length, controlledTime]);
  useEffect(() => {
    if (!ridePoints?.length) hasInitialized.current = false;
  }, [ridePoints?.length]);

  const currentMs = currentTime.getTime();
  const windowMs = windowMinutes * 60 * 1000;
  const windowStartMs = currentMs - windowMs;
  const maxCount = bins.reduce((max, b) => (b.count > max ? b.count : max), 1);

  const handleHistogramClick = useCallback(
    (e: React.MouseEvent<Element>) => {
      const el = e.currentTarget as HTMLElement;
      const rect = el.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const i = Math.floor((x / rect.width) * bins.length);
      const slot = bins[Math.max(0, Math.min(i, bins.length - 1))];
      if (slot) setCurrentTime(new Date(slot.timestampMs));
    },
    [bins, setCurrentTime]
  );

  const handleDrag = useCallback(
    (e: React.MouseEvent) => {
      if (e.buttons !== 1) return;
      handleHistogramClick(e);
    },
    [handleHistogramClick]
  );

  const formatTime = (d: Date) =>
    `${d.getUTCHours().toString().padStart(2, '0')}:${d.getUTCMinutes().toString().padStart(2, '0')}`;

  const formatSlotRange = (startMs: number) => {
    const endMs = startMs + 15 * 60 * 1000;
    return `${formatTime(new Date(startMs))} – ${formatTime(new Date(endMs))}`;
  };

  const hasData = bins.length > 0;

  const innerContent = (
    <>
      {/* Header */}
        <Box
          sx={{
            height: HEADER_HEIGHT,
            minHeight: HEADER_HEIGHT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid rgba(0,0,0,0.08)',
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
            <Typography
              sx={{
                fontFamily: FONT_FAMILY,
                fontSize: '1rem',
                fontWeight: 700,
                color: '#fff',
                letterSpacing: '0.02em',
              }}
            >
              {t('results.timeline.title')}
            </Typography>
            <Typography
              component="span"
              sx={{
                fontFamily: FONT_FAMILY,
                fontSize: '0.7rem',
                color: '#52565e',
                fontWeight: 500,
              }}
            >
              {t('results.timeline.utc')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              component="span"
              sx={{
                fontFamily: FONT_FAMILY,
                fontSize: '0.875rem',
                color: '#171a1f',
              }}
            >
              {t('results.timeline.speedLabel')}
            </Typography>
            <TextField
              select
              size="small"
              value={playSpeed}
              onChange={(e) => setPlaySpeed(Number(e.target.value))}
              inputProps={{ 'aria-label': t('results.timeline.speedLabel') }}
              sx={{
                width: 110,
                '& .MuiOutlinedInput-root': {
                  color: '#fff',
                  fontFamily: FONT_FAMILY,
                  fontSize: '0.875rem',
                  '& fieldset': { borderColor: 'rgba(0,0,0,0.18)' },
                  '&:hover fieldset': { borderColor: '#6b7280' },
                  '&.Mui-focused fieldset': { borderColor: ORANGE },
                },
                '& .MuiSvgIcon-root': { color: '#fff' },
              }}
            >
              {PLAY_SPEEDS.map((speed) => (
                <MenuItem key={speed} value={speed}>
                  {t('results.timeline.speedOption', { count: speed })}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'stretch', flex: 1, gap: 2, pt: 0.75, minHeight: 0 }}>
        {/* Left: Play/Pause + Time */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            minWidth: 200,
          }}
        >
          <IconButton
            onClick={handlePlayToggle}
            disabled={!hasData}
            sx={{
              color: ORANGE,
              backgroundColor: 'rgba(238, 115, 52, 0.15)',
              width: 56,
              height: 56,
              '&:hover': { backgroundColor: 'rgba(238, 115, 52, 0.25)' },
              '&.Mui-disabled': { color: 'rgba(0,0,0,0.18)' },
            }}
          >
            {isPlaying ? <Icon size={32} name="pause" /> : <Icon size={20} name="play" />}
          </IconButton>
          <Typography
            sx={{
              fontFamily: FONT_FAMILY,
              fontSize: '2.25rem',
              fontWeight: 800,
              color: '#fff',
              letterSpacing: '0.02em',
            }}
          >
            {formatTime(currentTime)}
          </Typography>
        </Box>

        {/* Right: Histogram scrubber + hourly labels - fills available height when embedded */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 80,
            py: 0.5,
            overflow: 'hidden',
          }}
        >
          {hasData ? (
            <>
              <Box
                onMouseDown={handleHistogramClick}
                onMouseMove={handleDrag}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-end',
                  gap: '2px',
                  width: '100%',
                  flex: 1,
                  minHeight: 60,
                  cursor: 'pointer',
                }}
              >
                {bins.map((slot) => {
                  const isInWindow =
                    slot.timestampMs >= windowStartMs && slot.timestampMs <= currentMs;
                  const heightPct = maxCount > 0 ? (slot.count / maxCount) * 100 : 0;
                  const tripLabel = t('results.timeline.tripsCount', { count: slot.count });
                  return (
                    <Tooltip
                      key={slot.timestampMs}
                      title={
                        <span style={{ fontFamily: FONT_FAMILY }}>
                          {formatSlotRange(slot.timestampMs)} · {tripLabel}
                        </span>
                      }
                      placement="top"
                      arrow
                      slotProps={{ popper: { sx: { fontFamily: FONT_FAMILY } } }}
                      sx={{ flex: 1, minWidth: 4, display: 'block' }}
                    >
                      <Box
                        sx={{
                          flex: 1,
                          minWidth: 4,
                          height: `${Math.max(4, heightPct)}%`,
                          backgroundColor: isInWindow ? ORANGE : 'rgba(0,0,0,0.15)',
                          borderRadius: '2px 2px 0 0',
                          transition: 'background-color 0.15s ease',
                        }}
                      />
                    </Tooltip>
                  );
                })}
              </Box>
              {/* Time labels under the histogram — interval scales with bin count */}
              <Box
                sx={{
                  display: 'flex',
                  gap: '2px',
                  width: '100%',
                  mt: 0.25,
                  flexShrink: 0,
                }}
              >
                {bins.map((slot) => {
                  const d = new Date(slot.timestampMs);
                  const totalHours = bins.length / 4; // 4 bins per hour
                  // Show a label every N hours so the row stays readable
                  const labelEveryHours = totalHours <= 12 ? 1 : totalHours <= 24 ? 2 : totalHours <= 48 ? 4 : 6;
                  const isLabelSlot = d.getUTCMinutes() === 0 && d.getUTCHours() % labelEveryHours === 0;
                  // Show the date on midnight ticks so multi-day data is navigable
                  const isMidnight = d.getUTCHours() === 0 && d.getUTCMinutes() === 0;
                  const labelText = isLabelSlot
                    ? isMidnight
                      ? `${(d.getUTCMonth() + 1).toString().padStart(2, '0')}/${d.getUTCDate().toString().padStart(2, '0')}`
                      : slot.label
                    : '';
                  return (
                    <Box
                      key={slot.timestampMs}
                      sx={{
                        flex: 1,
                        minWidth: 4,
                        textAlign: 'center',
                        fontFamily: FONT_FAMILY,
                        fontSize: '0.7rem',
                        color: isMidnight && isLabelSlot ? ORANGE : '#374151',
                        fontWeight: isMidnight && isLabelSlot ? 700 : 500,
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {labelText}
                    </Box>
                  );
                })}
              </Box>
            </>
          ) : (
            <Typography variant="body2" sx={{ color: '#6b7280', fontFamily: FONT_FAMILY }}>
              {t('results.timeline.noDataMessage')}
            </Typography>
          )}
        </Box>
        </Box>
    </>
  );

  if (embedded) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0,
          px: 2,
          pb: 0.5,
          pt: 0,
          fontFamily: FONT_FAMILY,
        }}
      >
        {innerContent}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 20,
        backgroundColor: TARMAC,
        fontFamily: FONT_FAMILY,
        boxShadow: '0 -4px 20px rgba(0,0,0,0.15)',
        transition: 'height 0.3s ease, min-height 0.3s ease',
        height: isExpanded ? EXPANDED_HEIGHT + TAB_HEIGHT + HEADER_HEIGHT : TAB_HEIGHT,
        minHeight: TAB_HEIGHT,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        onClick={() => setIsExpanded((e) => !e)}
        sx={{
          height: TAB_HEIGHT,
          minHeight: TAB_HEIGHT,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: TARMAC,
          borderTopLeftRadius: 10,
          borderTopRightRadius: 10,
          cursor: 'pointer',
          boxShadow: '0 -2px 8px rgba(0,0,0,0.2)',
          flexShrink: 0,
        }}
      >
        {isExpanded ? (
          <Icon size={28} name="chevron-down" style={{ color: '#fff'}} />
        ) : (
          <Icon size={28} name="chevron-up" style={{ color: '#fff'}} />
        )}
      </Box>
      <Box
        sx={{
          height: isExpanded ? EXPANDED_HEIGHT + HEADER_HEIGHT : 0,
          overflow: 'hidden',
          transition: 'height 0.3s ease',
          display: 'flex',
          flexDirection: 'column',
          px: 2,
          pb: 0.5,
          pt: 0,
          gap: 0,
        }}
      >
        {innerContent}
      </Box>
    </Box>
  );
};

export default TimelineControl;
